import {
  CacheLookupPolicy,
  InteractionRequiredAuthError,
  type AccountInfo,
  type RedirectRequest,
  type SilentRequest
} from '@azure/msal-browser';
import { initializeMsal, msalInstance, scopes } from 'config/authConfig';
import { env } from 'config/env';

let interactiveTokenRequest: Promise<void> | null = null;
let currentAccessToken: string | null = null;

const localDevAccessToken =
  'local-dev.' +
  btoa(
    JSON.stringify({
      transUniPermissions:
        '2000,2001,2002,2003,2004,3000,3001,3002,3003,3004,4000,5000,5001,6000,6001,6002,6003,6004,6100,6101,6102,6103,6104,6200,6201,6202,6203,6300,6301,6302,6400,6401,6402,6403,6500,6600,6700,6701,6702,6703,6704'
    })
  ) +
  '.local-dev';

const accessTokenListeners = new Set<(token: string | null) => void>();

export function subscribeToAccessTokenChanged(listener: (token: string | null) => void) {
  accessTokenListeners.add(listener);

  return () => {
    accessTokenListeners.delete(listener);
  };
}

function setCurrentAccessToken(token: string | null) {
  if (currentAccessToken === token) {
    return;
  }

  currentAccessToken = token;

  for (const listener of accessTokenListeners) {
    listener(token);
  }
}

export function getCurrentAccessToken() {
  return currentAccessToken;
}

export class InteractiveTokenAcquisitionStartedError extends Error {
  constructor() {
    super('Interactive token acquisition.');
    this.name = 'InteractiveTokenAcquisitionStartedError';
  }
}

export const isInteractiveTokenAcquisitionStartedError = (error: unknown): error is InteractiveTokenAcquisitionStartedError =>
  error instanceof InteractiveTokenAcquisitionStartedError;

const getTokenAccount = (): AccountInfo | null => {
  const activeAccount = msalInstance.getActiveAccount();
  if (activeAccount) {
    return activeAccount;
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    return null;
  }

  if (accounts.length > 1) {
    throw new Error('Multiple signed-in accounts were found.');
  }

  msalInstance.setActiveAccount(accounts[0]);
  return accounts[0];
};

const buildSilentRequest = (account: AccountInfo): SilentRequest => ({
  scopes,
  account,
  cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken
});

const isRecoverableSilentAuthError = (error: unknown): boolean => {
  const err = error as { name?: string; errorCode?: string } | undefined;

  return (
    error instanceof InteractionRequiredAuthError ||
    (err?.name === 'BrowserAuthError' && (err.errorCode === 'timed_out' || err.errorCode === 'monitor_window_timeout'))
  );
};

const startInteractiveTokenAcquisition = (account?: AccountInfo | null): never => {
  if (!interactiveTokenRequest) {
    const redirectRequest: RedirectRequest = {
      scopes,
      ...(account ? { account } : {})
    };

    interactiveTokenRequest = msalInstance
      .acquireTokenRedirect(redirectRequest)
      .catch((error) => {
        console.error('Failed to start interactive token acquisition.', error);
      })
      .finally(() => {
        interactiveTokenRequest = null;
      });
  }

  void interactiveTokenRequest;
  throw new InteractiveTokenAcquisitionStartedError();
};

export const acquireAccessToken = async (): Promise<string> => {
  if (env.VITE_APP_LOCAL_DEV_AUTH === 'true') {
    setCurrentAccessToken(localDevAccessToken);
    return localDevAccessToken;
  }

  await initializeMsal();

  const account = getTokenAccount();

  if (!account) {
    setCurrentAccessToken(null);
    return startInteractiveTokenAcquisition();
  }

  try {
    const authResponse = await msalInstance.acquireTokenSilent(buildSilentRequest(account));

    if (!authResponse.accessToken) {
      setCurrentAccessToken(null);
      throw new Error('MSAL did not return an access token.');
    }

    if (authResponse.account) {
      msalInstance.setActiveAccount(authResponse.account);
    }

    setCurrentAccessToken(authResponse.accessToken);

    return authResponse.accessToken;
  } catch (error) {
    if (isRecoverableSilentAuthError(error)) {
      setCurrentAccessToken(null);
      return startInteractiveTokenAcquisition(account);
    }

    setCurrentAccessToken(null);
    throw error;
  }
};
